import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axios";

const Signup = () => {
  const [form, setForm] = useState({
    storeAddress: "",
    suite: "",
    storeName: "",
    brandName: "",
    businessType: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    smsOptIn: true,
  });
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const value =
      target instanceof HTMLInputElement && target.type === "checkbox"
        ? target.checked
        : target.value;

    setForm({
      ...form,
      [target.name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await api.post("/signup/", form);
      navigate("/thank-you");
    } catch (err: any) {
      setError(err.response?.data?.error || "Signup failed. Please try again.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex bg-white font-sans">
      {/* Left: Form */}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-6 sm:px-12 max-w-2xl mx-auto py-8">
        <h1 className="text-3xl font-bold mb-1 text-black">
          Get started with FullPlateNow
        </h1>
        <p className="text-sm text-gray-500 mb-5">
          Centralize your delivery channels. Setup is quick, approval is
          personal.
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 text-sm px-4 py-2 rounded mb-4 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            name="storeAddress"
            type="text"
            placeholder="Store Address"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.storeAddress}
            onChange={handleChange}
            required
          />
          <input
            name="suite"
            type="text"
            placeholder="Floor / Suite (Optional)"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.suite}
            onChange={handleChange}
          />
          <input
            name="brandName"
            type="text"
            placeholder="Brand Name"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.brandName}
            onChange={handleChange}
            required
          />
          <select
            name="businessType"
            value={form.businessType}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            required
          >
            <option value="">Select Business Type...</option>
            <option value="Restaurant">Restaurant</option>
            <option value="Grocery Store">Grocery Store</option>
            <option value="Liquor Store">Liquor Store</option>
            <option value="Retail">Retail</option>
            <option value="Pharmacy">Pharmacy</option>
          </select>
          <div className="flex gap-4">
            <input
              name="firstName"
              type="text"
              placeholder="First Name"
              className="w-1/2 px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
              value={form.firstName}
              onChange={handleChange}
              required
            />
            <input
              name="lastName"
              type="text"
              placeholder="Last Name"
              className="w-1/2 px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
              value={form.lastName}
              onChange={handleChange}
              required
            />
          </div>
          <input
            name="email"
            type="email"
            placeholder="Email"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.email}
            onChange={handleChange}
            required
          />
          <input
            name="phone"
            type="tel"
            placeholder="Mobile Phone Number"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.phone}
            onChange={handleChange}
            required
          />
          <label className="flex items-start gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              name="smsOptIn"
              checked={form.smsOptIn}
              onChange={handleChange}
              className="mt-1 w-4 h-4"
            />
            <span>
              I agree to receive calls or SMS updates from FullPlateNow. Message
              and data rates may apply. Text STOP to opt out.
            </span>
          </label>
          <input
            name="password"
            type="password"
            placeholder="Password"
            className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A5F] transition-all duration-300"
            value={form.password}
            onChange={handleChange}
            required
          />
          <button
            type="submit"
            className="w-full bg-black text-white rounded-md px-6 py-3 font-semibold hover:bg-gray-900 transition-all duration-300 shadow-sm hover:shadow-md"
          >
            Create Account
          </button>
        </form>

        <div className="text-center text-sm mt-5">
          Already have an account?{" "}
          <Link
            to="/login"
            className="underline text-black hover:text-[#FF5A5F]"
          >
            Log in
          </Link>
        </div>
      </div>

      {/* Right: Restaurant Image */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#0d0d0d] via-[#1a1a1a] to-[#2a2a2a] items-center justify-center px-10 relative overflow-hidden">
        <div className="w-full h-full flex items-center justify-center">
          <img
            src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80"
            alt="Restaurant interior"
            className="w-full h-full object-cover opacity-70"
          />
        </div>
      </div>
    </div>
  );
};

export default Signup;
